import { toast } from 'react-toastify';
import { startRenewalSummary } from '@mutations/startRenewalSummary';
import { RENEWAL_SUMMARY_KEY } from '@queries/fetchRenewalSummary';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { get } from 'lodash-es';

const FALLBACK_ERROR = "Summary couldn't be started. Try again.";

/** 409 means a job is already running: not an error, the panel just starts polling it. */
const isAlreadyRunning = (error: unknown) => axios.isAxiosError(error) && error.response?.status === 409;

/** The 429 (daily AI budget) and 400 (not a renewal) answers carry a message written for the user. */
const serverMessage = (error: unknown): string =>
  (axios.isAxiosError(error) && (get(error, 'response.data.message') || get(error, 'response.data.error.message'))) ||
  FALLBACK_ERROR;

export const useStartRenewalSummary = (renewalId: string) => {
  const queryClient = useQueryClient();
  const refetchSummary = () => queryClient.invalidateQueries({ queryKey: RENEWAL_SUMMARY_KEY(renewalId) });

  const mutation = useMutation({
    mutationFn: () => startRenewalSummary(renewalId),
    // Hook-level: the job exists either way, so the GET must learn about it even if the panel closed.
    onSuccess: refetchSummary,
    onError: (error) => {
      if (isAlreadyRunning(error)) return refetchSummary();
      return undefined;
    },
  });

  const onStart = () =>
    mutation.mutate(undefined, {
      onError: (error) => {
        if (!isAlreadyRunning(error)) toast.error(serverMessage(error), { autoClose: 8_000 });
      },
    });

  return { onStart, isStarting: mutation.isPending };
};
